class ObsidianGallery {
  constructor(el) {
    this.el        = el;
    this.sectionId = el.dataset.sectionId;
    this.slides    = [...el.querySelectorAll('.obsidian-gallery__slide')];
    this.thumbs    = [...el.querySelectorAll('.obsidian-gallery__thumb')];
    this.thumbWrap = el.querySelector('.obsidian-gallery__thumbs');
    this.navUp     = el.querySelector('.obsidian-gallery__nav--up');
    this.navDown   = el.querySelector('.obsidian-gallery__nav--down');
    this.counterEl = el.querySelector('.obsidian-gallery__counter-num');
    this.zoomBtn   = el.querySelector('.obsidian-gallery__zoom');
    this.lightbox  = el.querySelector('.obsidian-gallery__lightbox');
    this.lbImg     = el.querySelector('.obsidian-gallery__lightbox-img');
    this.lbClose   = el.querySelector('.obsidian-gallery__lightbox-close');
    this.current   = 0;
    this.variants  = [];
    this.bind();
  }

  bind() {
    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.goTo(i));
    });

    this.navUp?.addEventListener('click', () => this.scrollRail(-1));
    this.navDown?.addEventListener('click', () => this.scrollRail(1));

    this.zoomBtn?.addEventListener('click', () => this.openLightbox());
    this.lbClose?.addEventListener('click', () => this.lightbox?.close());
    this.lightbox?.addEventListener('click', (e) => {
      if (e.target === this.lightbox) this.lightbox.close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.lightbox?.open) this.lightbox.close();
    });

    this.bindVariantSwitch();
  }

  bindVariantSwitch() {
    // Load the variant→media map embedded by Liquid in the gallery container
    const dataEl = document.getElementById(`ObsGalleryVariants-${this.sectionId}`);
    if (!dataEl) return;

    try {
      this.variants = JSON.parse(dataEl.textContent.trim());
    } catch {
      return;
    }

    if (!this.variants.length || this.slides.length < 2) return;

    // Find variant-selects: prefer explicit id, fall back to data-section attribute
    const variantSelects =
      document.getElementById(`variant-selects-${this.sectionId}`) ||
      document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);

    if (!variantSelects) return;

    // Primary listener: native change event bubbling from radio/select inputs
    variantSelects.addEventListener('change', () => {
      this.onVariantChange(variantSelects);
    });

    // Backup listener: Dawn's pub-sub event published by VariantSelects.connectedCallback.
    // Guards against edge cases where the change event doesn't reach the outer element
    // (e.g., swatch inputs with custom event handling, or future Dawn upgrades).
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
        // Only act when the change originated inside our section's variant-selects
        if (data?.event?.target && !variantSelects.contains(data.event.target)) return;
        this.onVariantChange(variantSelects);
      });
    }
  }

  onVariantChange(variantSelects) {
    // Read the currently selected value from each option group
    const selectedOptions = [...variantSelects.querySelectorAll('.product-form__input')]
      .map(group => {
        const radio = group.querySelector('input[type="radio"]:checked');
        if (radio) return radio.value.trim();
        const select = group.querySelector('select');
        if (select) return select.value.trim();
        return null;
      })
      .filter(Boolean);

    if (!selectedOptions.length) return;

    // Match selected options against the variant list (case-insensitive trim)
    const variant = this.variants.find(v =>
      Array.isArray(v.options) &&
      v.options.length === selectedOptions.length &&
      v.options.every((opt, i) =>
        String(opt).trim().toLowerCase() === selectedOptions[i].toLowerCase()
      )
    );

    if (!variant) return;
    this.goToVariantMedia(variant, selectedOptions[0]);
  }

  goToVariantMedia(variant, primaryOption) {
    // Tier 1: Shopify-assigned featured media (set per-variant in the Shopify Admin)
    if (variant.featured_media_id != null) {
      const idx = this.slides.findIndex(
        s => String(s.dataset.mediaId) === String(variant.featured_media_id)
      );
      if (idx !== -1) { this.goTo(idx); return; }
    }

    // Tier 2: image alt text contains the primary option value
    // Works when images are alt-tagged with variant names (e.g., "Black Obsidian", "Gold Sheen Obsidian")
    if (primaryOption) {
      const needle = primaryOption.toLowerCase();
      const idx = this.slides.findIndex(s => {
        const img = s.querySelector('img');
        return img?.alt && img.alt.toLowerCase().includes(needle);
      });
      if (idx !== -1) { this.goTo(idx); return; }
    }

    // Tier 3: variant position maps to slide order
    // Requires that product images are ordered to match variant order in Shopify Admin:
    //   variant 1 (position 1) → image 1, variant 2 → image 2, etc.
    const posIdx = (variant.position ?? 1) - 1;
    if (posIdx >= 0 && posIdx < this.slides.length) {
      this.goTo(posIdx);
    }
  }

  goTo(index) {
    if (index < 0 || index >= this.slides.length) return;

    this.slides[this.current].classList.remove('is-active');
    this.thumbs[this.current]?.classList.remove('is-active');

    this.current = index;

    this.slides[this.current].classList.add('is-active');
    this.thumbs[this.current]?.classList.add('is-active');

    if (this.counterEl) this.counterEl.textContent = this.current + 1;

    this.thumbs[this.current]?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: 'smooth',
    });
  }

  goToMedia(mediaId) {
    const idx = this.slides.findIndex(
      s => String(s.dataset.mediaId) === String(mediaId)
    );
    if (idx !== -1) this.goTo(idx);
  }

  scrollRail(direction) {
    if (!this.thumbWrap || !this.thumbs[0]) return;
    const isHorizontal = this.thumbWrap.scrollWidth > this.thumbWrap.clientWidth;
    if (isHorizontal) {
      this.thumbWrap.scrollBy({
        left: (this.thumbs[0].offsetWidth + 8) * direction,
        behavior: 'smooth',
      });
    } else {
      this.thumbWrap.scrollBy({
        top: (this.thumbs[0].offsetHeight + 8) * direction,
        behavior: 'smooth',
      });
    }
  }

  openLightbox() {
    if (!this.lightbox || !this.lbImg) return;
    const img = this.slides[this.current]?.querySelector('.obsidian-gallery__main-img');
    if (!img) return;
    this.lbImg.src = img.src.replace(/width=\d+/, 'width=2400');
    this.lbImg.alt = img.alt || '';
    this.lightbox.showModal();
  }
}

function initObsidianGalleries() {
  document.querySelectorAll('.obsidian-gallery').forEach((el) => {
    if (!el.dataset.galleryInit) {
      el.dataset.galleryInit = '1';
      new ObsidianGallery(el);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initObsidianGalleries);
} else {
  initObsidianGalleries();
}

document.addEventListener('shopify:section:load', () => {
  document.querySelectorAll('.obsidian-gallery').forEach(el => {
    delete el.dataset.galleryInit;
  });
  initObsidianGalleries();
});
